import resolveResponse from "contentful-resolve-response";
import { useEffect } from "react";
import eventClient from "@2u-landing-pages/utils/event-client";
import { pushCohesionAnonIdToSegment, validateAnonId } from "@2u-landing-pages/utils/validate-anon-id";
import { logCustomAttributes, logEvent } from "@red-digital/bricks";

declare global {
  interface Window {
    _Cohesion?: {
      sessionId?: string;
      anonymousId?: string;
      newAnonymousId?: boolean;
    };
    __monarchRequestStart?: number;
    newrelic?: {
      addPageAction?: (name: string, attributes?: Record<string, unknown>) => void;
    };
  }
}

export const useCohesionEvents = (
  lob: string,
  school: string,
  program: string,
  name: string,
  id: string
) => {
  useEffect(() => {
    logCustomAttributes([
      { name: "Release", value: process.env.RELEASE_VERSION || "" },
      { name: "LOB", value: lob || "" },
      { name: "School", value: school || "" },
      { name: "Program", value: program || "" },
    ]);

    if (window.cohesion) {
      window.cohesion("monarch:done", function (err: object | null, result: any) {
        const durationMs = window.__monarchRequestStart
          ? Math.round(performance.now() - window.__monarchRequestStart)
          : undefined;

        if (window?.newrelic?.addPageAction && durationMs !== undefined) {
          window.newrelic.addPageAction("monarch_request_timing", {
            durationMs,
            result: err ? "error" : "success",
            pageType: "2u_creative",
          });
        }

        if (err) {
          console.log("Monarch Error", err);

          eventClient.tempMonarchDecision({
            webElement: {
              location: "Monarch Error",
              name: name || "",
              htmlId: id || "",
            },
          });

          logEvent("monarch_error", {
            error: JSON.stringify(err),
          });

          return;
        }

        const monarchVariation = result?.source?.[school]?.value;
        let resolvedContent;
        if (monarchVariation) {
          resolvedContent = resolveResponse(monarchVariation)?.[0];

          // If we get a contentful response, log it to New Relic
          if (resolvedContent) {
            logEvent("monarch_decision", {
              variation: resolvedContent?.fields?.name || "",
              sysId: resolvedContent?.sys?.id || "",
            });
          }
        }

        eventClient.tempMonarchDecision({
          webElement: {
            location: "Decision Rendered",
            name: name || "",
            htmlId: id || "",
            text: resolvedContent?.fields?.name || "",
          },
        });
      });

      window.cohesion("ready", () => {
        logCustomAttributes([
          { name: "ClientSessionId", value: window?._Cohesion?.sessionId || "" },
        ]);
        pushCohesionAnonIdToSegment();
      });

      window.cohesion("consent:changed", function (groups: string[]) {
        const consentCaptured = Boolean(groups?.find((group: string) => group === "C0002"));
        if (consentCaptured) {
          pushCohesionAnonIdToSegment();
          validateAnonId();
          logEvent("consent_captured", {
            consentCaptured,
            groups: groups.join(", "),
          });
        }
      });

      window.cohesion("error", () => {
        eventClient.tempMonarchDecision({
          webElement: {
            location: "Cohesion Error",
            name: name || "",
            htmlId: id || "",
          },
        });
      });
    }
  }, []);
};
